import {
  ArdoqError,
  isArdoqError,
  Result,
  toArdoqError,
} from '@ardoq/common-helpers';
import { logError } from '@ardoq/logging';
import { catchError, filter, of, OperatorFunction, pipe, tap } from 'rxjs';

const isSuccess = <T>(x: Result<T>): x is T => !isArdoqError(x);

/**
 * If the observable is an error, handleError calls the `errorHandler`,
 * otherwise it returns the observable, cast to the correct type.
 * @param {errorHandler} [errorHandler = logError] - Gets called when the
 * observable is an `Error`, default is `logError`
 */
export const handleError = <T>(
  errorHandler: (error: ArdoqError) => void = logError
): OperatorFunction<Result<T>, T> =>
  pipe(
    wrapUncaughtErrors(),
    tap(error => {
      if (isArdoqError(error)) {
        errorHandler(error);
      }
    }),
    filter(isSuccess)
  );

/**
 * Stream operator that wraps any uncaught errors on the stream in `ArdoqError` and emits them as normal values.
 */
export const wrapUncaughtErrors = <T>(): OperatorFunction<T, T | ArdoqError> =>
  catchError((error: unknown) =>
    of(
      toArdoqError(
        error instanceof Error ? { error } : { error: new Error(String(error)) }
      )
    )
  );
